/**
 * Typed fetch wrapper for /api.
 *
 * Deliberately thin. Session lives in an httpOnly cookie set by Better Auth,
 * so there is no token handling here - `credentials: "include"` is the whole
 * auth story on the client.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: "GET", signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PATCH",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
};

// ------------------------------------------------------------------- types

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: "customer" | "staff" | "admin";
  image: string | null;
  updatedAt: string | number | null;
}

export interface AppConfig {
  addressRelease: string;
  user: SessionUser | null;
  /** [west, south, east, north] for a map with nothing else to fit. */
  mapStart: [number, number, number, number] | null;
  /** org: a saved address. geo: Cloudflare's location. */
  mapStartKind: "org" | "geo" | null;
  /** Printed when a campaign has not chosen its own return address. */
  returnAddress: string[];
  /** False while the placeholder is still in use. */
  returnAddressSet: boolean;
}

export interface CountStats {
  rollupRanges: number;
  boundaryRanges: number;
  rowsRead: number;
  degraded: boolean;
  ms: number;
  cached: boolean;
}

export interface CountResponse {
  total: number;
  residential: number;
  business: number;
  stats: CountStats;
}

export type Ring = [number, number][];

export interface Campaign {
  id: string;
  user_id?: string;
  customer_id?: string | null;
  name: string;
  status: string;
  previous_status?: string | null;
  source: "map" | "csv";
  selection: string;
  mail_type: "addressed" | "unaddressed";
  mail_speed: "regular" | "priority";
  audience?: "both" | "business" | "residential";
  product_id: string | null;
  artwork_id: string | null;
  design_id?: string | null;
  /** Library design this campaign was copied from. Null while it still uses that file. */
  design_source_id?: string | null;
  /** JSON array of 1-based artwork pages, one per campaign page. */
  artwork_pages?: string | null;
  address_count: number;
  res_count: number;
  biz_count: number;
  lodgement_date: number | null;
  /** Epoch seconds of a date waiting on eConnect. NULL when none is open. */
  requested_lodgement_date?: number | null;
  lodgement_requested_at?: number | null;
  approval_status: string;
  rejection_reason: string | null;
  order_id: string | null;
  order_status?: string | null;
  /** Epoch seconds when added to the cart. NULL when not in the cart. */
  in_cart_at?: number | null;
  country: string;
  state: string | null;
  created_at: number;
  updated_at: number;
  deleted_at?: number | null;
  creator_approved_at?: number | null;
  creator_approved_by?: string | null;
  print_approved_at?: number | null;
  print_approved_by?: string | null;
  rejected_at?: number | null;
  rejected_by?: string | null;
  cancelled_at?: number | null;
  cancelled_by?: string | null;
  counted_at?: number | null;
  salutation?: "householder" | "by_audience" | "resident" | "none" | "custom";
  salutation_custom?: string | null;
  /** JSON array of return-address lines. Null uses the eConnect default. */
  return_address?: string | null;
  proof_r2_key?: string | null;
  proof_at?: number | null;
  proof_fingerprint?: string | null;
  print_files_status?: "none" | "queued" | "running" | "done" | "failed";
  print_files_at?: number | null;
  /** Latest final-PDF build. Null when this campaign has never built one. */
  final_pdf_status?: string | null;
  final_pdf_updated_at?: number | null;
  /** Artwork id of the final PDF this campaign approved. */
  approved_artwork_id?: string | null;
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  organisation: string | null;
  phone: string | null;
  role: "customer" | "staff" | "admin";
  createdAt: string;
  image?: string | null;
  updatedAt?: string | number | null;
  campaigns?: number;
}

export interface Notice {
  id: string;
  title: string;
  body: string;
  url: string;
  createdAt: string;
  readAt: string | null;
  deletedAt: string | null;
  kind: string;
  event: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorImage: string | null;
  actorUpdatedAt: string | number | null;
}

export interface Profile extends Customer {
  emailVerified: number;
  /** 1 opens the map on Cloudflare's location. 0 uses the saved address. */
  use_my_location: number;
}

export interface CustomerAddress {
  id: string;
  full_name: string | null;
  company: string | null;
  location_name: string | null;
  street_line1: string;
  street_line2: string | null;
  city: string;
  province: string | null;
  postal_code: string;
  country_code: string;
  phone: string | null;
  default_shipping: number;
  default_billing: number;
  /** 1 frames the campaign map on this address. At most one per user. */
  use_for_location: number;
  created_at: number;
  updated_at: number;
}

export interface ZoneAddress {
  id: number;
  label: string;
  locality: string | null;
  state: string | null;
  postcode: string | null;
  lng: number;
  lat: number;
}

export interface ZonePreset {
  id: string;
  owner_id: string;
  name: string;
  kind: "polygon" | "rectangle" | "circle";
  /** JSON ring array, the same shape as CampaignZone.rings. */
  rings: string;
  /** 1 pins this preset to the top of the customer's list. */
  favorite: number;
  created_at: number;
  updated_at: number;
}

/** A zone still sitting on one of the customer's other campaigns. */
export interface UsedZone {
  id: string;
  campaign_id: string;
  campaign_name: string;
  label: string | null;
  kind: "polygon" | "rectangle" | "circle" | "named";
  rings: string | null;
  zone_kind: string | null;
  zone_code: string | null;
  exclude: number;
  created_at: number;
}

export interface CampaignZone {
  id: string;
  campaign_id: string;
  label: string | null;
  kind: "polygon" | "rectangle" | "circle" | "named";
  rings: string | null;
  /** `loc:<addressId>:circle|rectangle` when the zone was set from a saved location. */
  origin: string | null;
  zone_kind: string | null;
  zone_code: string | null;
  res_count: number;
  biz_count: number;
  exclude: number;
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  width_mm: number;
  height_mm: number;
  gsm: number | null;
  stock: string | null;
  active?: number;
  /** Paper size label from Vendure: 'DL' | 'MaxPop' | 'A4'. */
  size: string | null;
  pages: number;
  addressed_mail: number;
  unaddressed_mail: number;
  image: string | null;
  sort_order: number;
}

export interface PriceTierRow {
  product_id: string;
  min_qty: number;
  unit_cents: number;
}

export interface QuoteLine {
  kind: string;
  description: string;
  quantity: number;
  unitCents: number;
  totalCents: number;
}

export interface Quote {
  lines: QuoteLine[];
  printCents: number;
  postageCents: number;
  taxCents: number;
  totalCents: number;
  unitCents: number;
  barcodeSavingCents: number;
}

export interface ZoneOption {
  kind: string;
  code: string;
  name: string;
  state: string | null;
  res: number;
  biz: number;
  west: number | null;
  south: number | null;
  east: number | null;
  north: number | null;
  /** 'derived' | 'mock' | 'official'. Mock rows must not be quoted. */
  provenance: string;
  level: string | null;
  sub_type: string | null;
}

export interface Artwork {
  id: string;
  user_id?: string;
  name: string;
  content_type: string;
  bytes: number | null;
  page_count: number | null;
  width_mm: number | null;
  height_mm: number | null;
  orientation: string | null;
  /** Source design in the DIY Design Tool, when this PDF was exported from one. */
  design_id?: string | null;
  /** Design version this PDF was rendered from. Null for an upload. */
  design_version?: number | null;
  /** Steps when that design is rendered again. Uploads stay at 1. */
  version?: number;
  /** Set on a campaign snapshot. Those rows stay out of the library list. */
  campaign_id?: string | null;
  /** Library artwork this snapshot was copied from. */
  source_artwork_id?: string | null;
  /** Library version at the time of the copy. */
  source_version?: number | null;
  created_at: number;
  /** NULL for rows seeded before the column existed. */
  updated_at: number | null;
}

export interface Order {
  id: string;
  code: string;
  status: string;
  currency: string;
  total_cents: number;
  print_cents: number;
  postage_cents: number;
  tax_cents: number;
  quantity: number;
  campaign_id: string | null;
  user_id?: string;
  placed_at: number | null;
  created_at: number;
}
